import { cloneDeep, pick } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getMaterialAssets } from '@/i18n/assets';
import { Cell, CellStyle, LayerType, TextAlignType } from '@/version-types';
import { TablePreset } from '.';
import presetImage from './assets/preset4.png';

const PRESET_ID = 'c79b3441';

const getTableByPreset: TablePreset['create'] = () => {
    return {
        id: uuidv4(),
        layerType: LayerType.TABLE,
        width: 990,
        height: 594,
        rotate: 0,
        materialIds: {},
        opacity: 1,
        title: '',
        fitWidth: 0,
        fitHeight: 0,
        extraInfo: {
            lockRatio: false,
        },
        data: {
            presetId: PRESET_ID,
            screenshotUrl: getMaterialAssets().tableLayer.screenshotUrl.table4,
            cells: [
                [
                    {
                        hIndex: 0,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['2'],
                    },
                    {
                        hIndex: 1,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['3'],
                    },
                    {
                        hIndex: 2,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['4'],
                    },
                    {
                        hIndex: 3,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['2'],
                    },
                    {
                        hIndex: 1,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['3'],
                    },
                    {
                        hIndex: 2,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['4'],
                    },
                    {
                        hIndex: 3,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['2'],
                    },
                    {
                        hIndex: 1,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['3'],
                    },
                    {
                        hIndex: 2,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['4'],
                    },
                    {
                        hIndex: 3,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['2'],
                    },
                    {
                        hIndex: 1,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['3'],
                    },
                    {
                        hIndex: 2,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: ['4'],
                    },
                    {
                        hIndex: 3,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
            ],
            baseCells: {
                horizontal: [246.69473684210527, 246.69473684210527, 246.69473684210527, 246.69473684210527],
                vertical: [147.50526315789475, 147.50526315789475, 147.50526315789475, 147.50526315789475],
            },
            allStyleId: '1',
            styles: {
                '1': {
                    fontSize: 30,
                    backgroundColor: '#ffffffff',
                    bold: true,
                    color: '#000000ff',
                    textAlign: TextAlignType.CENTER,
                    border: {
                        top: {
                            color: '#b9b9b9ff',
                            style: 'line-0',
                            width: 2,
                        },
                        right: {
                            color: '#b9b9b9ff',
                            style: 'line-0',
                            width: 2,
                        },
                        bottom: {
                            color: '#b9b9b9ff',
                            style: 'line-0',
                            width: 2,
                        },
                        left: {
                            color: '#b9b9b9ff',
                            style: 'line-0',
                            width: 2,
                        },
                    },
                },
                '2': {
                    backgroundColor: '#dedfe9ff',
                },
                '3': {
                    backgroundColor: '#eaecf3ff',
                },
                '4': {
                    backgroundColor: '#f6f7fbff',
                },
            },
        },
        lock: false,
        children: [],
        shine: true,
        hidden: false,
    };
};

const tablePreset: TablePreset = {
    id: PRESET_ID,
    preview: presetImage,
    create: getTableByPreset,
    overwriteStyle: (oldTableLayer) => {
        const newTableLayer = cloneDeep(oldTableLayer);

        const col1StyleId = `${Math.max(0, ...Object.keys(newTableLayer.data.styles).map(Number)) + 1}`;
        const col2StyleId = `${+col1StyleId + 1}`;
        const col3StyleId = `${+col1StyleId + 2}`;
        const col4StyleId = `${+col1StyleId + 3}`;
        const cells = newTableLayer.data.cells.flat().filter(Boolean) as Cell[];
        cells.forEach((c) => {
            let styleId: string;
            switch (c.hIndex) {
                case 0:
                    styleId = col1StyleId;
                    break;
                case 1:
                    styleId = col2StyleId;
                    break;
                case 2:
                    styleId = col3StyleId;
                    break;
                default:
                    styleId = col4StyleId;
                    break;
            }
            c.styleIds.push(styleId);
        });
        const presetDemo = getTableByPreset();
        const bodyStyle: CellStyle = pick(presetDemo.data.styles['1'], ['border', 'backgroundColor']);
        newTableLayer.data.styles[col4StyleId] = bodyStyle;
        newTableLayer.data.styles[col1StyleId] = {
            ...bodyStyle,
            ...pick(presetDemo.data.styles['2'], ['border', 'backgroundColor']),
        };
        newTableLayer.data.styles[col2StyleId] = {
            ...bodyStyle,
            ...pick(presetDemo.data.styles['3'], ['border', 'backgroundColor']),
        };
        newTableLayer.data.styles[col3StyleId] = {
            ...bodyStyle,
            ...pick(presetDemo.data.styles['4'], ['border', 'backgroundColor']),
        };

        return newTableLayer;
    },
};

export default tablePreset;
